// src/features/data/membership-heal-tab.tsx
// IR-903 — observability for the WhatsApp stale-past membership backlog.
import { Kicker } from "@/components/iris";
import { ChartCard, chartColor } from "@/features/statistics/chart-primitives";
import { useMembershipHealBacklog } from "@/hooks/use-data";
import type { MembershipHealSnapshot } from "@/lib/types";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const ALERT_THRESHOLD_24H = 200;

// ─── Pure helpers (exported for unit tests) ─────────────────────────

export function totalStale24h(snap: MembershipHealSnapshot): number {
  return (snap.stale_past_24h ?? 0) + (snap.stale_possibly_left_24h ?? 0);
}

export function totalStale7d(snap: MembershipHealSnapshot): number {
  return (snap.stale_past_7d ?? 0) + (snap.stale_possibly_left_7d ?? 0);
}

interface ChartRow {
  date: string;
  ts: number;
  [envKey: string]: string | number;
}

export function buildChartRows(history: Record<string, MembershipHealSnapshot[]>): ChartRow[] {
  const byDay = new Map<string, ChartRow>();
  for (const env of Object.keys(history)) {
    for (const snap of history[env] ?? []) {
      const d = new Date(snap.computed_at);
      if (Number.isNaN(d.getTime())) continue;
      const key = d.toISOString().slice(0, 10);
      const ts = Date.parse(`${key}T00:00:00Z`);
      const row = byDay.get(key) ?? { date: key, ts };
      row[env] = totalStale24h(snap);
      byDay.set(key, row);
    }
  }
  return Array.from(byDay.values()).sort((a, b) => a.ts - b.ts);
}

// ─── KPI cards ──────────────────────────────────────────────────────

function HealKpiCards({
  current,
}: {
  current: Record<string, MembershipHealSnapshot>;
}) {
  const envs = Object.keys(current).sort();
  if (envs.length === 0) {
    return (
      <div className="iris-card" style={{ padding: 16, color: "var(--iris-fg-3)", fontSize: 12 }}>
        No snapshots yet — daily cron runs at 02:30 UTC. Trigger manually with{" "}
        <code style={{ background: "rgba(255,255,255,0.04)", padding: "1px 4px" }}>
          python3 manage.py take_membership_heal_snapshot
        </code>
        .
      </div>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${envs.length}, minmax(0,1fr))`,
        gap: 12,
      }}
    >
      {envs.map((env) => {
        const snap = current[env];
        if (!snap) return null;
        const total24h = totalStale24h(snap);
        const breaching = total24h > ALERT_THRESHOLD_24H;
        return (
          <div key={env} className="iris-card" style={{ padding: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <Kicker variant="muted">{env.toUpperCase()} · stale 24h</Kicker>
              {breaching ? (
                <AlertTriangle size={14} style={{ color: "var(--iris-neon-red)" }} />
              ) : (
                <CheckCircle2 size={14} style={{ color: "var(--iris-neon-green)" }} />
              )}
            </div>
            <div className="iris-kpi" style={{ fontSize: 26, marginBottom: 4 }}>
              {total24h.toLocaleString()}
            </div>
            <div className="iris-meta" style={{ display: "flex", gap: 12, fontSize: 11 }}>
              <span>past: {snap.stale_past_24h.toLocaleString()}</span>
              <span>possibly: {snap.stale_possibly_left_24h.toLocaleString()}</span>
            </div>
            <div
              className="iris-meta"
              style={{ marginTop: 8, fontSize: 11, color: "var(--iris-fg-4)" }}
            >
              7d: {totalStale7d(snap).toLocaleString()} · groups:{" "}
              {snap.total_groups.toLocaleString()} · active: {snap.total_active.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Trend chart ────────────────────────────────────────────────────

function HealTrendChart({
  history,
}: {
  history: Record<string, MembershipHealSnapshot[]>;
}) {
  const data = buildChartRows(history);
  const envs = Object.keys(history).sort();

  if (data.length === 0) {
    return (
      <ChartCard title="Stale-past membership trend (last 30d)" subtitle="No snapshots in window">
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--iris-fg-4)",
            fontSize: 12,
          }}
        >
          Waiting for first snapshot…
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="Stale-past membership trend (last 30d)"
      subtitle={`Sum of membership_status IN ('2','3') with last_scrape_time within 24h. Threshold ${ALERT_THRESHOLD_24H}.`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.04)" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: "#6b7184" }}
            stroke="rgba(255,255,255,0.1)"
          />
          <YAxis tick={{ fontSize: 10, fill: "#6b7184" }} stroke="rgba(255,255,255,0.1)" />
          <Tooltip
            contentStyle={{
              background: "var(--iris-bg-3)",
              border: "1px solid var(--iris-line-2)",
              borderRadius: "var(--iris-r-sm)",
              fontSize: 11,
              color: "var(--iris-fg-2)",
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine
            y={ALERT_THRESHOLD_24H}
            stroke="#ff3355"
            strokeDasharray="4 2"
            label={{
              value: `alert ${ALERT_THRESHOLD_24H}`,
              position: "right",
              fill: "#ff3355",
              fontSize: 10,
            }}
          />
          {envs.map((env, i) => (
            <Line
              key={env}
              type="monotone"
              dataKey={env}
              name={env.toUpperCase()}
              stroke={chartColor(i)}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ─── Tab ────────────────────────────────────────────────────────────

export function MembershipHealTab() {
  const { data, isLoading, refetch, isFetching, error } = useMembershipHealBacklog(30, "whatsapp");

  if (isLoading) {
    return (
      <div className="iris-card" style={{ padding: "24px 18px" }}>
        <div style={{ color: "var(--iris-fg-3)", fontSize: 12 }}>
          Loading membership heal backlog…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          borderRadius: "var(--iris-r-lg)",
          border: "1px solid rgba(255,77,210,0.2)",
          background: "rgba(255,77,210,0.06)",
          padding: "16px 18px",
          color: "var(--iris-neon-pink)",
          fontSize: 12,
        }}
      >
        Failed to load: {String(error)}
      </div>
    );
  }

  const current = data?.current ?? {};
  const history = data?.history ?? {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <Kicker>WhatsApp Membership Heal Backlog</Kicker>
          <p
            className="iris-meta"
            style={{ marginTop: 4, marginBottom: 0, color: "var(--iris-fg-3)", fontSize: 12 }}
          >
            IR-903: members observed in a recent WA scrape but still tagged confirmed_left or
            possibly_left. Healthy steady-state should be near zero; pp-group-membership-verify
            heals these on each daily run.
          </p>
        </div>
        <button
          type="button"
          className="iris-btn"
          onClick={() => refetch()}
          disabled={isFetching}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <RefreshCw size={12} style={{ opacity: isFetching ? 0.4 : 1 }} />
          Refresh
        </button>
      </div>

      <HealKpiCards current={current} />
      <HealTrendChart history={history} />
    </div>
  );
}
